import { useCallback, useEffect, useRef, useState } from 'react'
import Modal from '@mui/material/Modal'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'

import OtpInput, { OtpInputHandle } from './otpInput'
import useCountdown from '../../hooks/useCountdown'
import useResendCooldown from '../../hooks/useResendCooldown'
import { useNotification } from '../../Notification'
import validarOtp from '../../utils/otp/validateOtp'
import resendOtp from '../../utils/otp/resendOtp'
import {
  CanaisOtp,
  OtpInitData,
  OtpModalCallbacks,
  ValidateOtpOk,
  ResendOtpOk,
  isOtpError,
} from '../../types/otp'

/** Máximo de reenvios controlado localmente (backend também limita). */
const MAX_REENVIOS = 3

interface OtpModalProps extends OtpModalCallbacks {
  open: boolean
  initData: OtpInitData
}

const estiloContainer = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 420 },
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
  outline: 'none',
}

function mensagemCanais(canais: CanaisOtp): string {
  if (canais.sms && canais.email) return 'Enviamos o código no seu sms e email'
  if (canais.sms) return 'Enviamos o código no seu sms'
  if (canais.email) return 'Enviamos o código no seu email'
  return 'Erro no envio do código. Tente novamente!'
}

export default function OtpModal({
  open,
  initData,
  onSuccess,
  onResend,
  onCancel,
  onError,
}: OtpModalProps) {
  const { notify } = useNotification()
  const inputRef = useRef<OtpInputHandle>(null)

  const [canais, setCanais] = useState<CanaisOtp>(initData.canais)
  const [expiraEm, setExpiraEm] = useState<string>(initData.expira_em)
  const [reenviosRestantes, setReenviosRestantes] = useState<number>(MAX_REENVIOS)
  const [jaReenviou, setJaReenviou] = useState<boolean>(false)
  const [validando, setValidando] = useState<boolean>(false)
  const [reenviando, setReenviando] = useState<boolean>(false)

  const { formatado, expirado } = useCountdown(expiraEm)
  const cooldown = useResendCooldown(60)

  useEffect(() => {
    setCanais(initData.canais)
    setExpiraEm(initData.expira_em)
    setReenviosRestantes(MAX_REENVIOS)
    setJaReenviou(false)
    setValidando(false)
    setReenviando(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initData.otp_session_token])

  const handleValidar = useCallback(
    async (codigo: string) => {
      if (validando) return
      setValidando(true)
      try {
        const { status, body } = await validarOtp({
          otp_session_token: initData.otp_session_token,
          codigo,
        })

        if (status === 200 && !isOtpError(body)) {
          onSuccess(body as ValidateOtpOk)
          return
        }

        const message = isOtpError(body)
          ? body.message
          : 'Não foi possível validar o código.'

        if (status === 401) {
          notify(message, 'error')
          inputRef.current?.limparEFocar()
        } else {
          notify(message, 'error')
          onError(message)
        }
      } catch (erro) {
        console.error(erro)
        notify('Falha de conexão ao validar o código. Tente novamente.', 'error')
        inputRef.current?.limparEFocar()
      } finally {
        setValidando(false)
      }
    },
    [validando, initData.otp_session_token, notify, onSuccess, onError]
  )

  const handleReenviar = useCallback(async () => {
    if (reenviando || cooldown.emCooldown || reenviosRestantes <= 0) return
    setReenviando(true)
    try {
      const { status, body } = await resendOtp({
        otp_session_token: initData.otp_session_token,
      })

      if (status === 200 && !isOtpError(body)) {
        const ok = body as ResendOtpOk
        setCanais(ok.canais)
        setExpiraEm(ok.expira_em)
        setReenviosRestantes((prev) => prev - 1)
        setJaReenviou(true)
        cooldown.reiniciar()
        onResend(ok.canais, ok.expira_em)
      } else {
        const message = isOtpError(body)
          ? body.message
          : 'Não foi possível reenviar o código.'
        notify(message, 'warning')
      }
    } catch (erro) {
      console.error(erro)
      notify('Falha de conexão ao reenviar o código. Tente novamente.', 'error')
    } finally {
      setReenviando(false)
    }
  }, [
    reenviando,
    cooldown,
    reenviosRestantes,
    initData.otp_session_token,
    notify,
    onResend,
  ])

  const handleClose = useCallback(() => {
    if (validando || reenviando) return
    onCancel()
  }, [validando, reenviando, onCancel])

  const inputDesabilitado = validando || reenviando || expirado
  const reenviarDesabilitado =
    reenviando || validando || cooldown.emCooldown || reenviosRestantes <= 0

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="otp-titulo"
      className="otpModal"
    >
      <Box sx={estiloContainer}>
        <Typography
          className="otpTitulo"
          variant="h6"
          component="h2"
          sx={{ mb: 1, textAlign: 'center' }}
        >
          Confirme o código
        </Typography>

        <Typography
          variant="body2"
          sx={{ mb: 3, textAlign: 'center' }}
        >
          {mensagemCanais(canais)}
        </Typography>

        <OtpInput
          ref={inputRef}
          onComplete={handleValidar}
          disabled={inputDesabilitado}
        />

        <Box
          sx={{
            mt: 2,
            minHeight: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {validando ? (
            <CircularProgress size={20} />
          ) : expirado ? (
            <Typography variant="body2" color="error">
              Código expirado, reenvie o código
            </Typography>
          ) : (
            <Typography variant="body2">
              O código expira em {formatado}
            </Typography>
          )}
        </Box>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Button
            variant="text"
            onClick={handleReenviar}
            disabled={reenviarDesabilitado}
          >
            {cooldown.emCooldown
              ? `Reenviar código (${cooldown.segundosRestantes}s)`
              : 'Reenviar código'}
          </Button>

          {jaReenviou && (
            <Typography
              variant="caption"
              display="block"
              sx={{ mt: 0.5, fontSize: '10px' }}
            >
              Reenvios restantes: {reenviosRestantes}
            </Typography>
          )}
        </Box>

        {reenviosRestantes <= 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Você atingiu o limite de reenvios.
          </Alert>
        )}
      </Box>
    </Modal>
  )
}